import { useNavigate } from "react-router";
import styles from "./header.module.css";
import { BREADCRUMB_PATH_MAPPER } from "@/utils/global-constants";
import Divider from "../divider";
import { useTranslation } from "@/utils/i18n/client";
import { BASIC_ROLES_BY_FAIL, UserRole, getHomeHref } from "@/utils/enum/user-role";

interface BreadcrumbItem {
  label: string;
  href?: string;
  isActive?: boolean;
  icon?: React.ReactNode;
}

function splitPathSegments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

function matchSegments(path: string, key: string, mode: "exact" | "startsWith"): string | null {
  const pathSegments = splitPathSegments(path);
  const keySegments = splitPathSegments(key);

  if (mode === "exact" && pathSegments.length !== keySegments.length) {
    return null;
  }

  if (keySegments.length > pathSegments.length) {
    return null;
  }

  const matches = keySegments.every((keySegment, index) => {
    return keySegment === "*" || keySegment === pathSegments[index];
  });

  if (!matches) {
    return null;
  }

  return `/${pathSegments.slice(0, keySegments.length).join("/")}`;
}

export function matchesBreadcrumbPattern(path: string, key: string): string | null {
  return matchSegments(path, key, "exact");
}

export function matchesBreadcrumbPatternStartsWith(path: string, key: string): string | null {
  return matchSegments(path, key, "startsWith");
}

export const Breadcrumb: React.FC<{ pathname: string; userRole?: UserRole | null }> = ({
  pathname,
  userRole,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // React Compiler가 자동으로 최적화
  const generateBreadcrumbs = (path: string): BreadcrumbItem[] => {
    // 역할에 따른 홈 경로 설정
    const result: BreadcrumbItem[] = [
      {
        label: t("breadcrumbs.home"),
        href: getHomeHref(userRole ?? BASIC_ROLES_BY_FAIL),
        isActive: true,
      },
    ];

    const pathSegmentsCount = path.split("/").filter(Boolean).length;

    // pathMapper의 key 순서대로 검사
    for (const key of Object.keys(BREADCRUMB_PATH_MAPPER)) {
      if (key === "/home" || key === "/admin/home") continue; // 홈 경로는 이미 추가됨

      // 현재 경로보다 세그먼트가 더 긴 키는 매칭 시도하지 않음
      const keySegmentsCount = key.split("/").filter(Boolean).length;
      if (keySegmentsCount > pathSegmentsCount) {
        continue;
      }

      const pushBreadcrumb = (newPath: string, key: string, isActive: boolean) => {
        result.push({
          label: t(BREADCRUMB_PATH_MAPPER[key as keyof typeof BREADCRUMB_PATH_MAPPER]),
          href: newPath,
          isActive: isActive, // 현재 페이지와 다르면 활성화 (클릭 가능)
        });
      };

      const exactMatch = matchesPatternExact(path, key);
      const patternMatch = matchesBreadcrumbPattern(path, key);
      const startWithMatch = matchesBreadcrumbPatternStartsWith(path, key);
      const isActive = path !== key && !matchesBreadcrumbPattern(path, key);

      // 순서를 변경하지 않습니다.
      if (exactMatch) {
        pushBreadcrumb(key, key, isActive);
      } else if (patternMatch) {
        pushBreadcrumb(patternMatch, key, isActive);
      } else if (startWithMatch) {
        pushBreadcrumb(startWithMatch, key, isActive);
      }

      // 마지막으로 추가된 항목의 세그먼트 수가 현재 경로와 같거나 많다면 더 이상 매칭 시도하지 않음
      const lastHref = result[result.length - 1]?.href ?? "";
      const lastSegmentsCount = lastHref.split("/").filter(Boolean).length;
      if (lastSegmentsCount >= pathSegmentsCount) {
        break;
      }
    }

    //TODO:- 임시
    // 마지막 요소의 isActive를 false로 설정
    if (result.length > 0) {
      result[result.length - 1].isActive = false;
    }
    return result;
  };

  const breadcrumbs = generateBreadcrumbs(pathname);

  //MARK:- Validation
  function matchesPatternExact(path: string, key: string): string | null {
    const testResult = path.startsWith(key);
    if (testResult) {
      return path;
    }
    return null;
  }

  //MARK:- Bt action
  const handleBreadcrumbClick = (href: string) => {
    console.log("[Breadcrumb] :: handleBreadcrumbClick :: href", href);
    navigate(href);
  };

  //MARK: - return
  return (
    <div className={styles.breadcrumbContainer}>
      <div className={styles.breadcrumbWrapper}>
        {breadcrumbs.map((item, index) => (
          <span key={index}>
            {item.href ? (
              item.isActive ? (
                <button
                  type="button"
                  className={styles.breadcrumbButtonActive}
                  onClick={() => handleBreadcrumbClick(item.href!)}
                >
                  {item.label}
                </button>
              ) : (
                <span className={styles.breadcrumbButtonDisabled}>{item.label}</span>
              )
            ) : (
              <span className={styles.breadcrumbButtonDisabled}>{item.label}</span>
            )}
            {index < breadcrumbs.length - 1 && (
              <span className={styles.breadcrumbSeparator}>/</span>
            )}
          </span>
        ))}
      </div>
      <Divider color="var(--color-warm-grey-94)" />
    </div>
  );
};
